import { useState, useEffect } from 'react';
import ChartCard from '../ui/ChartCard';

interface LiveEvent {
  id: number;
  title?: string;
  started_at: string;
  ended_at?: string;
  duration_minutes?: number;
  notes?: string;
  created_at: string;
  updated_at: string;
}

interface LiveEventsCardProps {
  username: string;
}

const formatDateTime = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
};

const formatDuration = (minutes: number | null | undefined) => {
  if (!minutes) return 'Ongoing';
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours > 0) return `${hours}h ${mins}m`;
  return `${mins}m`;
};

export default function LiveEventsCard({ username }: LiveEventsCardProps) {
  const [events, setEvents] = useState<LiveEvent[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formData, setFormData] = useState({
    title: '',
    started_at: '',
    ended_at: '',
    notes: ''
  });
  const [error, setError] = useState<string | null>(null);

  const fetchEvents = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch(`/api/analytics/live-events?username=${encodeURIComponent(username)}`, {
        credentials: 'include'
      });
      const data = await response.json();
      if (data.success) {
        setEvents(data.events);
      } else {
        setError('Failed to load live events');
      }
    } catch (err) {
      setError('Error loading live events');
      console.error('Failed to fetch live events:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (username) {
      fetchEvents();
    }
  }, [username]);

  const resetForm = () => {
    setFormData({ title: '', started_at: '', ended_at: '', notes: '' });
    setIsAdding(false);
    setEditingId(null);
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!formData.started_at) {
      setError('Start time is required');
      return;
    }

    try {
      if (editingId) {
        // Update existing event
        const response = await fetch(`/api/analytics/live-events/${editingId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            title: formData.title || null,
            started_at: formData.started_at,
            ended_at: formData.ended_at || null,
            notes: formData.notes || null
          })
        });
        const data = await response.json();
        if (!data.success) throw new Error(data.error || 'Failed to update');
      } else {
        // Create new event
        const response = await fetch('/api/analytics/live-events', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            username,
            title: formData.title || null,
            started_at: formData.started_at,
            ended_at: formData.ended_at || null,
            notes: formData.notes || null
          })
        });
        const data = await response.json();
        if (!data.success) throw new Error(data.error || 'Failed to create');
      }

      await fetchEvents();
      resetForm();
    } catch (err: any) {
      setError(err.message || 'Failed to save event');
    }
  };

  const handleEdit = (event: LiveEvent) => {
    setFormData({
      title: event.title || '',
      started_at: event.started_at.slice(0, 16), // Format for datetime-local input
      ended_at: event.ended_at ? event.ended_at.slice(0, 16) : '',
      notes: event.notes || ''
    });
    setEditingId(event.id);
    setIsAdding(true);
  };

  const handleDelete = async (id: number) => {
    if (!confirm('Delete this live event?')) return;

    try {
      const response = await fetch(`/api/analytics/live-events/${id}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      const data = await response.json();
      if (data.success) {
        await fetchEvents();
      } else {
        setError('Failed to delete event');
      }
    } catch (err) {
      setError('Error deleting event');
    }
  };

  return (
    <ChartCard 
      title="Live Stream Events" 
      subtitle="Track your TikTok Live sessions to correlate with growth"
      actions={
        !isAdding && (
          <button
            onClick={() => setIsAdding(true)}
            className="px-3 py-1.5 text-sm bg-rose-600 hover:bg-rose-500 text-white rounded-lg transition font-medium"
          >
            + Add Event
          </button>
        )
      }
      isLoading={isLoading}
    >
      <div className="space-y-4">
        {error && (
          <div className="text-sm text-rose-400 bg-rose-500/10 px-3 py-2 rounded-lg">
            {error}
          </div>
        )}

        {isAdding && (
          <form onSubmit={handleSubmit} className="bg-slate-800/50 p-4 rounded-xl space-y-3 border border-slate-700">
            <div className="flex justify-between items-center mb-2">
              <h4 className="text-sm font-semibold text-white">
                {editingId ? 'Edit Event' : 'New Live Event'}
              </h4>
              <button
                type="button"
                onClick={resetForm}
                className="text-slate-400 hover:text-white text-xl"
              >
                ×
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-slate-400 mb-1">Title (optional)</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="e.g., Q&A Session"
                  className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm focus:outline-none focus:border-rose-500"
                />
              </div>

              <div>
                <label className="block text-xs text-slate-400 mb-1">Started At *</label>
                <input
                  type="datetime-local"
                  value={formData.started_at}
                  onChange={(e) => setFormData({ ...formData, started_at: e.target.value })}
                  required
                  className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm focus:outline-none focus:border-rose-500"
                />
              </div>

              <div>
                <label className="block text-xs text-slate-400 mb-1">Ended At (optional)</label>
                <input
                  type="datetime-local"
                  value={formData.ended_at}
                  onChange={(e) => setFormData({ ...formData, ended_at: e.target.value })}
                  className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm focus:outline-none focus:border-rose-500"
                />
              </div>

              <div>
                <label className="block text-xs text-slate-400 mb-1">Notes (optional)</label>
                <input
                  type="text"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="e.g., High engagement"
                  className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm focus:outline-none focus:border-rose-500"
                />
              </div>
            </div>

            <div className="flex gap-2 pt-2">
              <button
                type="submit"
                className="flex-1 px-4 py-2 bg-rose-600 hover:bg-rose-500 text-white rounded-lg transition font-medium text-sm"
              >
                {editingId ? 'Update' : 'Save'}
              </button>
              <button
                type="button"
                onClick={resetForm}
                className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition font-medium text-sm"
              >
                Cancel
              </button>
            </div>
          </form>
        )}

        {events.length === 0 && !isAdding && !isLoading && (
          <div className="text-center py-8 text-slate-400 text-sm">
            <svg className="w-12 h-12 mx-auto mb-3 opacity-50" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15.75 10.5l4.72-4.72a.75.75 0 011.28.53v11.38a.75.75 0 01-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 002.25-2.25v-9a2.25 2.25 0 00-2.25-2.25h-9A2.25 2.25 0 002.25 7.5v9a2.25 2.25 0 002.25 2.25z" />
            </svg>
            <p>No live events recorded yet</p>
            <p className="text-xs mt-1">Track your live streams to analyze their impact</p>
          </div>
        )}

        <div className="space-y-2 max-h-96 overflow-y-auto">
          {events.map((event) => (
            <div
              key={event.id}
              className="bg-slate-800/30 p-3 rounded-lg border border-slate-700/50 hover:border-slate-600 transition group"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    {event.title && (
                      <h5 className="font-semibold text-white text-sm truncate">
                        {event.title}
                      </h5>
                    )}
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      event.ended_at 
                        ? 'bg-slate-700 text-slate-300' 
                        : 'bg-rose-600/20 text-rose-400'
                    }`}>
                      {formatDuration(event.duration_minutes)}
                    </span>
                  </div>
                  <p className="text-xs text-slate-400">
                    {formatDateTime(event.started_at)}
                    {event.ended_at && ` → ${formatDateTime(event.ended_at)}`}
                  </p>
                  {event.notes && (
                    <p className="text-xs text-slate-500 mt-1 line-clamp-2">
                      {event.notes}
                    </p>
                  )}
                </div>

                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition">
                  <button
                    onClick={() => handleEdit(event)}
                    className="p-1.5 hover:bg-slate-700 rounded text-slate-400 hover:text-white transition"
                    title="Edit"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </button>
                  <button
                    onClick={() => handleDelete(event.id)}
                    className="p-1.5 hover:bg-rose-600/20 rounded text-slate-400 hover:text-rose-400 transition"
                    title="Delete"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </ChartCard>
  );
}
